import { type RefObject, useEffect, useRef } from "react";
import { mergeKeepNonNullOrUndefined } from "../utils/objectManipulation";
import { isDef } from "../utils/types";
import useComputedStyles from "./useComputedStyle";
import useElementStyles from "./useElementStyles";

/*
• useMutationAndResizeStyle combines useElementStyles and useComputedStyles to provide a more comprehensive style observation.
It uses both MutationObserver and ResizeObserver to detect changes in both inline styles and computed styles.
This approach ensures that both inline and computed styles are observed, providing a more accurate representation of the element's layout.
*/
const useMutationAndResizeStyle = (
  containerRef?: RefObject<HTMLElement | null>,
  getPropertyStyles?: string[],
  getComputedStyles?: string[],
  delay = 40,
  enable = true,
  style?: React.CSSProperties,
  callback?: (styles: any) => void,
  debug = false
) => {
  // Use ref to track previous styles to avoid unnecessary callback calls
  const prevStylesRef = useRef<string>("");
  // Use ref for callback to avoid effect re-running when callback identity changes
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  // Determine if hooks should be enabled (containerRef must exist)
  const hooksEnabled = !!containerRef && enable;

  // Get styles that trigger re-render when 'height' changes
  const elementStyles = useElementStyles(hooksEnabled, containerRef, getPropertyStyles, delay, debug);

  // Get computed styles based on 'visible' flag
  const computedStyles = useComputedStyles(hooksEnabled && (isDef(enable) ?? false), containerRef, getComputedStyles, [enable], undefined, debug);

  // Merge styles, keeping non-null or undefined values
  const mergedStyles = mergeKeepNonNullOrUndefined(mergeKeepNonNullOrUndefined(computedStyles, elementStyles), style);

  // Call callback in useEffect to avoid setState during render
  useEffect(() => {
    if (hooksEnabled && callbackRef.current) {
      const stylesString = JSON.stringify(mergedStyles);
      // Only call callback if styles have changed
      if (stylesString !== prevStylesRef.current) {
        prevStylesRef.current = stylesString;
        callbackRef.current(mergedStyles);
      }
    }
  }, [hooksEnabled, mergedStyles]);

  if (!containerRef) return {};
  return mergedStyles;
};

export default useMutationAndResizeStyle;
